import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { useLocalizedSettings } from '../../hooks/useLocalizedSettings';
import { useScrollToHash } from '../../hooks/useScrollToHash';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { localizePath, stripLangPrefix } from '../../utils/i18nPath';
import Logo from '../../assets/images/logo.webp';
import { IoLogoFacebook, IoLogoInstagram } from 'react-icons/io';
import { IoCloseOutline, IoMenuOutline, IoChevronForwardOutline, IoLocationOutline, IoTimeOutline, IoCallOutline } from 'react-icons/io5';
import { NAV_LINKS } from './navLinks';
import { HomeLink } from './HomeLink';

/**
 * Shared page header for sub-pages (Prices, Gallery, etc.).
 * Includes full navbar with desktop links and mobile burger menu.
 */
export const PageHeader = () => {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const { settings, s, phoneHref } = useLocalizedSettings();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    const toggleMenu = useCallback(() => setMenuOpen(prev => !prev), []);
    const closeMenu = useCallback(() => setMenuOpen(false), []);

    // Smooth-scroll to a section, navigating home first if needed.
    // Polls (rAF) for the target element rather than racing on a setTimeout.
    const handleHashClick = useScrollToHash(closeMenu);

    // Close mobile menu on resize to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) setMenuOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Lock body scroll when menu is open
    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);




    // Check active state by comparing the canonical (UA) path of both sides
    // — prevents /en/prices being treated as a different page from /prices.
    const currentBase = stripLangPrefix(location.pathname);
    const isActive = (href) => href === currentBase;

    // Build a localized URL for a route href ('/prices' → '/en/prices' in EN).
    const toLocalizedHref = (href) => localizePath(href, language);

    return (
        <>
            {/* Top info bar */}
            <div className="hidden lg:block bg-navy text-white text-sm">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-10">
                    <div className="flex items-center space-x-6">
                        <span className="flex items-center space-x-2">
                            <IoLocationOutline className="text-gold" />
                            <span>{s('address')}</span>
                        </span>
                        <span className="flex items-center space-x-2">
                            <IoTimeOutline className="text-gold" />
                            <span>{s('topBarHours')}</span>
                        </span>
                    </div>
                    <div className="flex items-center space-x-4">
                        <a href={phoneHref} className="flex items-center space-x-2 hover:text-gold transition-colors">
                            <IoCallOutline className="text-gold" />
                            <span>{settings.phone}</span>
                        </a>
                        {settings.facebookUrl && (
                            <a href={settings.facebookUrl} aria-label="Facebook" rel="noopener noreferrer" target="_blank" className="hover:text-gold transition-colors">
                                <IoLogoFacebook size="18px" />
                            </a>
                        )}
                        {settings.instagramUrl && (
                            <a href={settings.instagramUrl} aria-label="Instagram" rel="noopener noreferrer" target="_blank" className="hover:text-gold transition-colors">
                                <IoLogoInstagram size="18px" />
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Navbar */}
            <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 sm:h-20 lg:h-20">
                        {/* Logo */}
                        <HomeLink to={toLocalizedHref('/')} className="flex-shrink-0 w-28 sm:w-36 lg:w-48">
                            <img className="w-full h-auto" src={Logo} alt="UDent logo" />
                        </HomeLink>

                        {/* Desktop nav links */}
                        <nav className="hidden lg:flex items-center space-x-8 font-semibold text-[15px]" aria-label="Main navigation">
                            {NAV_LINKS.map(({ key, href, isRoute }) => {
                                const linkClass = `transition-colors whitespace-nowrap relative after:content-[''] after:absolute after:h-0.5 after:bg-gold after:left-0 after:-bottom-1 after:transition-all ${
                                    isActive(href)
                                        ? 'text-gold after:w-full'
                                        : 'text-gray-700 hover:text-gold after:w-0 hover:after:w-full'
                                }`;
                                if (!isRoute) {
                                    return (
                                        <a key={key} href={toLocalizedHref(href)} onClick={(event) => handleHashClick(event, href)} className={linkClass}>
                                            {t(key)}
                                        </a>
                                    );
                                }
                                const LinkComponent = href === '/' ? HomeLink : Link;
                                return (
                                    <LinkComponent key={key} to={toLocalizedHref(href)} className={linkClass}>
                                        {t(key)}
                                    </LinkComponent>
                                );
                            })}
                        </nav>

                        {/* Desktop right */}
                        <div className="hidden lg:flex items-center space-x-5">
                            <LanguageSwitcher />
                            <a href={phoneHref}
                                className="px-6 py-2.5 bg-gold text-white font-semibold rounded-full hover:bg-gold-dark transition-colors shadow-md hover:shadow-lg">
                                {settings.phone}
                            </a>
                        </div>

                        {/* Mobile: language + hamburger */}
                        <div className="flex items-center space-x-3 lg:hidden">
                            <LanguageSwitcher />
                            <button onClick={toggleMenu}
                                className="p-2 text-gray-700 hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold/40 rounded-md transition-colors"
                                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                                aria-expanded={menuOpen}>
                                {menuOpen ? <IoCloseOutline size="28px" /> : <IoMenuOutline size="28px" />}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile menu */}
            {/* Overlay backdrop */}
            <div
                className={`fixed inset-0 bg-black/50 z-[60] lg:hidden transition-opacity duration-300 ${
                    menuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                onClick={closeMenu}
            />

            {/* Slide-in panel */}
            {/* `invisible`/`visible` rather than an inline style: an inline
                style attribute gets normalised differently by the pre-render
                HTML minifier ("visibility:hidden") and by React on the client
                ("visibility: hidden;"), which breaks hydration. */}
            <div
                className={`fixed top-0 right-0 h-full w-72 sm:w-80 bg-white z-[70] shadow-2xl transform transition-transform duration-300 ease-in-out lg:hidden ${
                    menuOpen ? 'translate-x-0 visible' : 'translate-x-full invisible'
                }`}
            >
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <img className="w-24 h-auto" src={Logo} alt="UDent" />
                    <button onClick={closeMenu} className="p-2 text-gray-600 hover:text-gold transition-colors" aria-label="Close menu">
                        <IoCloseOutline size="28px" />
                    </button>
                </div>
                <nav className="flex flex-col px-6 pt-4 space-y-1" aria-label="Mobile navigation">
                    {NAV_LINKS.map(({ key, href, isRoute }) => {
                        const navClass = `flex items-center justify-between py-3 text-lg font-semibold border-b border-gray-100 transition-colors ${
                            isActive(href) ? 'text-gold' : 'text-gray-700 hover:text-gold'
                        }`;
                        if (!isRoute) {
                            return (
                                <a key={key} href={toLocalizedHref(href)} onClick={(event) => handleHashClick(event, href)} className={navClass}>
                                    <span>{t(key)}</span>
                                    <IoChevronForwardOutline className="text-gray-400" size="16px" />
                                </a>
                            );
                        }
                        const LinkComponent = href === '/' ? HomeLink : Link;
                        return (
                            <LinkComponent key={key} to={toLocalizedHref(href)} onClick={closeMenu} className={navClass}>
                                <span>{t(key)}</span>
                                <IoChevronForwardOutline className="text-gray-400" size="16px" />
                            </LinkComponent>
                        );
                    })}
                </nav>
                <div className="px-6 mt-8 space-y-5">
                    <a href={phoneHref}
                        className="block w-full text-center py-3 font-bold bg-gold text-white rounded-full hover:bg-gold-dark transition-colors shadow-md">
                        {settings.phone}
                    </a>
                    <div className="flex items-center justify-center space-x-6">
                        {settings.facebookUrl && (
                            <a href={settings.facebookUrl} aria-label="Facebook" rel="noopener noreferrer" target="_blank" className="text-navy hover:opacity-70 transition-opacity">
                                <IoLogoFacebook size="28px" />
                            </a>
                        )}
                        {settings.instagramUrl && (
                            <a href={settings.instagramUrl} aria-label="Instagram" rel="noopener noreferrer" target="_blank" className="text-navy hover:opacity-70 transition-opacity">
                                <IoLogoInstagram size="28px" />
                            </a>
                        )}
                    </div>
                </div>
            </div>

        </>
    );
};
